/**
 * What the frame loop reads back out of the sim for the things that are
 * neither the picture nor the HUD: the damage smoke and the audio's distance
 * model. Split out of `flight-scene.ts`, which owns the loop — these take a
 * runner and a pose and hand back plain data, so they are testable without a
 * GPU.
 */

import { controlsLocked, type EncounterState, type GunState, type PilotState, type Pose, type Vec3 } from "@red-opus/engine";
import type { EffectSpawn } from "./encounter-views.ts";
import type { EncounterRunner } from "./sim-runner.ts";
import { airframeDamageFraction, type SmokeSource } from "./smoke-trail.ts";
import { WORKING_PLUME_FRACTION } from "./particle-sources.ts";

// The particle path's own source records live next door (`particle-sources.ts`,
// split out at aislop's 400-line ceiling) and are re-exported here so no
// consumer moved.
export type { BurningAircraft, FireRunner } from "./particle-sources.ts";
export {
  SCENERY_FLAME_ANCHOR_M,
  SCENERY_PLUME_INTENSITY,
  TRAIN_PLUME_INTENSITY,
  TRAIN_STACK_FORWARD_M,
  TRAIN_STACK_HEIGHT_M,
  WORKING_PLUME_FRACTION,
  collectBurningAircraft,
  collectSceneryFires,
  collectTrainPlumes,
  emptyBurningAircraft,
} from "./particle-sources.ts";
// AD-014c's crash fires are a second sibling, split for the same reason.
export {
  CRASH_FLAME_ANCHOR_M,
  CRASH_PLUME_INTENSITY,
  CRASH_SPREAD_M,
  collectCrashFires,
} from "./crash-sources.ts";

import type { FireRunner } from "./particle-sources.ts";

/**
 * Which entities a frame's sprite lists should cover.
 *
 * ENHANCED EFFECTS (`particle-effects.ts`) draws burning and hurt **aeroplanes**
 * as particles instead of sprites, and nothing must be drawn twice — a
 * particle flame and an `EXPLO` billboard on the same airframe read as a
 * smeared mess, which is the exact failure the 2026-08-22 smoke lesson measured
 * from the other direction. So the toggle's on-state passes `omitAircraft`
 * here and the sprite path stops seeing them.
 *
 * `omitTrainPlume` is the same contract for the one other thing the particles
 * draw: a **working train's** coal plume, which ENHANCED EFFECTS also renders
 * as particles (`collectTrainPlumes` below). Burning targets stay sprite in
 * both states whatever this says — a gasbag going up and a train burning out
 * are the original's own art playing its own sequences, out of the toggle's
 * scope exactly as they were before the train joined the particle path.
 *
 * Default is `false` everywhere, so a caller that does not pass options gets
 * exactly the lists it got before ENHANCED EFFECTS existed. That is what makes
 * "toggle off = today's pixels" a property of the code rather than a promise.
 */
export interface FrameSourceOptions {
  readonly omitAircraft?: boolean;
  readonly omitTrainPlume?: boolean;
}

/**
 * Slant range to the nearest other aeroplane, or `null` with the sky to
 * yourself — what keys the original's distance model (a second OPL2 voice
 * whose level steps down a measured ladder as an aircraft closes; see
 * `audio/engine.ts`'s `nearestEnemyRangeM`). Read off the interpolated render
 * views rather than the raw encounter so it tracks what is actually on screen.
 */
/**
 * Whether rounds are genuinely leaving the guns this frame, for
 * `audio/engine.ts`'s `firing` param.
 *
 * The ammo/jam checks alone are not the whole story: `flight/step.ts` runs
 * every tick's inputs through `derelictInputs` (AD-014) before they ever
 * reach `guns.ts`, so a pilot who is dead or blacked out has his trigger
 * force-released *inside the sim* — `guns.burstRounds` truthfully stops, and
 * with it the tracers and muzzle flash (`muzzleFlashVisible` keys off
 * `burstRounds`, honestly). `triggerFiring` here is the raw, un-sanitised
 * trigger read straight off the input device, which still reads held — a
 * player's hand does not know the pilot slumped over the stick — so without
 * this check the gate stays open on ammo/jam state that never moved and the
 * gunfire crack loops over a dead man's stick. Deliberately reuses
 * `controlsLocked` rather than re-deriving pilot incapacity locally, so this
 * can never drift from what `derelictInputs` actually silenced.
 */
export function gunAudioFiring(triggerFiring: boolean, guns: GunState, pilot: PilotState): boolean {
  return triggerFiring && guns.rounds > 0 && !guns.jammed && !controlsLocked(pilot);
}

/**
 * Range to the nearest living **hostile** aeroplane, or `null` for an empty sky.
 *
 * Separate from `nearestOtherAircraftM` below rather than a flag on it, because
 * the two have genuinely different consumers: the audio engine wants the
 * nearest aeroplane of *any* side (a wingman's engine is audible), and the
 * time-compression gate wants an enemy. `time-compression.ts` has always said
 * so — *"a transit-skipping feature has exactly one sim-relevant reason to
 * [refuse]: an enemy is already close enough to fight"* — and until G44 it was
 * handed the side-blind number, which is a different quantity wearing the
 * doc's words.
 *
 * **Measured, over the nineteen catalogue missions at seed 3**: the side-blind
 * gate refuses compression *at spawn* on thirteen of them, and on seven of
 * those the nearest aeroplane is a **friendly one 136–401 m away** — the
 * player's own formation, or the machines he was briefed to escort. Those are
 * precisely the sorties with a long transit leg to skip, so the feature was
 * dead where it was most wanted, and G44's autopilot would have been dead with
 * it. Reads the encounter rather than `runner.others()` because `AircraftView`
 * carries no side.
 */
export function nearestHostileAircraftM(encounter: EncounterState, playerId: string): number | null {
  const player = encounter.aircraft.find((entry) => entry.id === playerId);
  if (!player) return null;
  let nearest: number | null = null;
  for (const entry of encounter.aircraft) {
    if (entry.id === playerId || entry.status === "destroyed" || entry.side === player.side) continue;
    const range = Math.hypot(
      entry.sim.position.x - player.sim.position.x,
      entry.sim.position.y - player.sim.position.y,
      entry.sim.position.z - player.sim.position.z,
    );
    if (nearest === null || range < nearest) nearest = range;
  }
  return nearest;
}

export function nearestOtherAircraftM(runner: EncounterRunner, pose: Pose): number | null {
  let nearest: number | null = null;
  for (const view of runner.others()) {
    if (view.status === "destroyed") continue;
    const range = Math.hypot(
      view.pose.position.x - pose.position.x,
      view.pose.position.y - pose.position.y,
      view.pose.position.z - pose.position.z,
    );
    if (nearest === null || range < nearest) nearest = range;
  }
  return nearest;
}

/**
 * Everything in the world that is leaving a trail — the player included, any
 * balloon or Zeppelin currently alight, and any train under way. A gasbag has
 * no engine to lose, so its only smoke condition is `combat/targets.ts`'s
 * ignition state, which is exactly the one an incendiary belt sets; a train
 * smokes because it is a train.
 */
export function smokeSourcesFor(runner: FireRunner, pose: Pose, options: FrameSourceOptions = {}): SmokeSource[] {
  const player = options.omitAircraft === true ? undefined : runner.encounter().aircraft.find((entry) => entry.ai === null);
  const sources: SmokeSource[] = (options.omitAircraft === true ? [] : runner.others()).map((view) => ({
    x: view.pose.position.x,
    y: view.pose.position.y,
    z: view.pose.position.z,
    damageFraction: view.damageFraction,
    burning: view.burning,
  }));
  for (const target of runner.targets()) {
    if (target.destroyed) continue;
    if (target.burning) {
      sources.push({
        x: target.position.x,
        y: target.position.y,
        z: target.position.z,
        damageFraction: 1,
        burning: true,
      });
      continue;
    }
    // A working train's plume. Not damage — it is the locomotive doing its job,
    // which the original attaches at construction and stops only when the train
    // retires. Thin, because it should read as a smudge on the landscape that
    // rewards looking down, not as something already on fire.
    //
    // ENHANCED EFFECTS draws this plume as particles instead (`collectTrainPlumes`),
    // so its on-state passes `omitTrainPlume` and the sprite path stops seeing it —
    // the same one-owner rule `omitAircraft` enforces for aeroplanes.
    if (!target.smoking) continue;
    if (options.omitTrainPlume === true) continue;
    sources.push({
      x: target.position.x,
      y: target.position.y,
      z: target.position.z,
      damageFraction: WORKING_PLUME_FRACTION,
      burning: false,
    });
  }
  if (player) {
    sources.push({
      x: pose.position.x,
      y: pose.position.y,
      z: pose.position.z,
      damageFraction: airframeDamageFraction(player.sim.damage),
      burning: player.fire.burning,
    });
  }
  return sources;
}

/**
 * Everything currently **alight and still in the sky** — where the renderer
 * holds a flame (`sprite-effects.ts`'s `writeFires`).
 *
 * Fire is not the same list as smoke, in both directions. A wreck's plume keeps
 * feeding the smoke pool after its aeroplane is gone (`effect-sprites.ts`), but
 * a flame hanging in empty air where something blew up several seconds ago is
 * not something the original draws — the explosion already played. And an
 * aeroplane that is merely hurt smokes without burning. So this is the
 * intersection: burning, and not destroyed.
 *
 * The player is read off `fire.burning`, which is the *same* field
 * `combat-hud.ts` reports and therefore the same one the audio engine's fire bed
 * is gated on — sight and sound cannot disagree about whether you are alight.
 *
 * Takes the three accessors it reads rather than the whole runner: it states
 * what the function depends on, and it means the rule can be tested against a
 * hand-built roster instead of a fight flown until something catches fire.
 */
export function fireSourcesFor(runner: FireRunner, pose: Pose, options: FrameSourceOptions = {}): Vec3[] {
  const omitAircraft = options.omitAircraft === true;
  const player = omitAircraft ? undefined : runner.encounter().aircraft.find((entry) => entry.ai === null);
  const fires: Vec3[] = (omitAircraft ? [] : runner.others())
    .filter((view) => view.burning && view.status !== "destroyed")
    .map((view) => ({ x: view.pose.position.x, y: view.pose.position.y, z: view.pose.position.z }));
  for (const target of runner.targets()) {
    if (target.burning && !target.destroyed) fires.push({ ...target.position });
  }
  if (player?.fire.burning && player.status !== "destroyed") fires.push({ ...pose.position });
  return fires;
}

/**
 * Audio's half of "sight and sound off the same list" for flak: distance in metres from the
 * player to each `flakBurst`-kind spawn in this frame's `effects` (the exact list
 * `sprites.spawn` already drew a burst for) — what `audio/engine.ts`'s
 * `ContinuousFrameParams.flakBurstDistancesM` / `flak-audio.ts`'s `FlakBurstVoicePool` need.
 */
export function flakBurstDistancesFor(effects: readonly EffectSpawn[], pose: Pose): number[] {
  return effects
    .filter((spawn) => spawn.kind === "flakBurst")
    .map((spawn) => Math.hypot(spawn.x - pose.position.x, spawn.y - pose.position.y, spawn.z - pose.position.z));
}

/**
 * Slant range in metres to every *other* aeroplane whose guns fired this frame —
 * what `audio/enemy-gun-audio.ts` turns into distance-attenuated cracks of
 * `SIMSNDS-04`, the cue a live OPL2 capture identifies as the guns of an
 * aeroplane that is not yours (`docs/formats/sound-music.md` § "Gunfire, jams
 * and hits").
 *
 * **Every other aeroplane, not only hostile ones**, and that is the deliberate
 * reading rather than an oversight. The original carries exactly two gun cues —
 * cue 1 for the player's own and cue 4 for somebody else's — and no third cue a
 * wingman could use; and the distance mechanism cue 4 shares is `engine-voice.ts`'s
 * `ENEMY_LEVEL_LADDER`, which the capture describes as the ladder *other aircraft*
 * are stepped through, friend and foe alike (`nearestOtherAircraftM` above is
 * scoped the same way for the same reason). No capture shows a wingman firing, so
 * the claim being made here is only that the original has one cue for "not your
 * guns" — the falloff curve is what keeps a distant friendly from being heard.
 *
 * A destroyed aeroplane is skipped on the same rule the engine ladder uses: the
 * wreck is no longer a shooter.
 */
export function firingOtherAircraftRangesM(runner: Pick<EncounterRunner, "others">, pose: Pose): number[] {
  const ranges: number[] = [];
  for (const view of runner.others()) {
    if (view.status === "destroyed" || view.firingNow !== true) continue;
    ranges.push(
      Math.hypot(
        view.pose.position.x - pose.position.x,
        view.pose.position.y - pose.position.y,
        view.pose.position.z - pose.position.z,
      ),
    );
  }
  return ranges;
}

/** Nothing on the water or the gasbag is shooting — a module constant, not an `[]` allocated 60 times a second. */
const NO_BATTERY_RANGES: readonly number[] = [];

/**
 * How recently a battery must have put a round up to count as *firing now*, in
 * sim ticks.
 *
 * The aeroplanes' own signal is an exact per-tick edge (`AircraftView.firingNow`
 * differences `roundsFired` across the two states the runner interpolates
 * between); a battery has no such view, so this reads the rounds themselves and
 * needs a window rather than an instant — a frame does not observe every tick,
 * and a station's cadence does not put a round on every tick either.
 *
 * **Four ticks, and the bound is measured at both ends.** A defensive station
 * fires at `gunRoundsPerSecond × AIRSHIP_RATE_OF_FIRE_FRACTION` ≈ 18.3
 * rounds/s, i.e. 0.304 a tick, so any four consecutive ticks of an engaged
 * station carry one round or two and never none — the window cannot miss a
 * gunner who is shooting. And 4 ticks is 66.7 ms against the mix's own 118 ms
 * `GUN_SHOT_INTERVAL_S`, so the cracks stop inside one retrigger interval of the
 * guns stopping — the window cannot leave a battery sounding after it has ceased
 * fire either.
 */
const BATTERY_FIRING_WINDOW_TICKS = 4;

/**
 * The three fields of an `EncounterState` this signal actually reads. Stated as
 * a type for the reason `fireSourcesFor` takes its accessors rather than a whole
 * runner: it says what the rule depends on, and it lets the rule be tested
 * against a posed battery instead of a fight flown until a ship opens up. An
 * `EncounterState` satisfies it structurally.
 */
export interface BatteryFireState {
  readonly tick: number;
  readonly rounds: readonly { readonly shooterId: string; readonly spawnTick: number }[];
  readonly targets: readonly {
    readonly id: string;
    readonly position: Vec3;
    readonly destroyed: boolean;
  }[];
}

/**
 * Slant range in metres to every **battery** that put rounds up in the last few
 * ticks — the escort sloop's two Maxims and a Zeppelin's four, which between
 * them are everything in this port that shoots and is not an aeroplane.
 *
 * It feeds the same pool `firingOtherAircraftRangesM` does, and deliberately:
 * `SIMSNDS-04` is the original's cue for *guns that are not yours* — its bank
 * program already sits 6.7 dB under the player's own cue 1 (`audio-plan.md`
 * §571) — and the original carries no third gun cue a warship could have. A
 * defensive Maxim in a splinter tub firing at the player is the situation that
 * cue names, so it gets that cue at that level, distance-attenuated by
 * `enemyGunGain` exactly as a Fokker's guns are. Nothing here adds gain.
 *
 * **Read off `EncounterState.rounds` rather than off a view**, because a ship is
 * not on `state.aircraft` and is filtered out of `targetViewsOf` (she has no
 * baked model — `target-profiles.ts` draws her from `data/enhanced`), so neither
 * roster the renderer already builds can answer the question. The rounds can:
 * they carry `shooterId` and `spawnTick`, and the shooter is looked up in
 * `state.targets`, which is what makes this list *batteries only* — an
 * aeroplane's id is never in it, so no shooter is counted twice.
 *
 * A destroyed hull is skipped on the same rule the aeroplanes' list uses. It
 * cannot fire (`shipFightsOn`, and `airshipPass`'s burning/destroyed filter),
 * but its last burst outlives it by up to `roundLifetimeTicks`, and a wreck is
 * not a shooter.
 */
export function firingBatteryRangesM(encounter: BatteryFireState, pose: Pose): readonly number[] {
  if (encounter.rounds.length === 0) return NO_BATTERY_RANGES;
  let ranges: number[] | null = null;
  for (const target of encounter.targets) {
    if (target.destroyed) continue;
    const fired = encounter.rounds.some(
      (round) => round.shooterId === target.id && encounter.tick - round.spawnTick <= BATTERY_FIRING_WINDOW_TICKS,
    );
    if (!fired) continue;
    ranges ??= [];
    ranges.push(
      Math.hypot(
        target.position.x - pose.position.x,
        target.position.y - pose.position.y,
        target.position.z - pose.position.z,
      ),
    );
  }
  return ranges ?? NO_BATTERY_RANGES;
}

/**
 * Every gun that is not the player's and fired this frame, as slant ranges —
 * the aeroplanes and the batteries in one list, which is what
 * `audio/enemy-gun-audio.ts`'s pool wants: it ranks its three voices
 * nearest-first, and a sloop 300 m below the fight has as much claim on one as
 * a Fokker 900 m astern.
 */
export function firingGunRangesM(runner: Pick<EncounterRunner, "others" | "encounter">, pose: Pose): number[] {
  const ranges = firingOtherAircraftRangesM(runner, pose);
  for (const rangeM of firingBatteryRangesM(runner.encounter(), pose)) ranges.push(rangeM);
  return ranges;
}
